import { useState } from 'react'
import { toast } from 'sonner'
import { useAlertRules, useUpdateRule, type AlertRule } from '@/lib/alerts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'

function RuleRow({ rule }: { rule: AlertRule }) {
  const updateMut = useUpdateRule()
  const [enabled, setEnabled] = useState(rule.enabled)
  const [channels, setChannels] = useState(rule.channels.join(', '))
  const [paramsJson, setParamsJson] = useState(JSON.stringify(rule.params, null, 2))

  async function handleSave() {
    let params: Record<string, unknown>
    try {
      params = JSON.parse(paramsJson) as Record<string, unknown>
    } catch {
      toast.error('Invalid JSON in params')
      return
    }
    try {
      await updateMut.mutateAsync({
        id: rule.id,
        body: {
          enabled,
          channels: channels.split(',').map((c) => c.trim()).filter(Boolean),
          params,
        },
      })
      toast.success(`Rule "${rule.name}" saved`)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Save failed')
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center justify-between">
          <span>{rule.name}</span>
          <span className="text-xs text-muted-foreground font-normal">{rule.severity}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {rule.description && (
          <p className="text-sm text-muted-foreground">{rule.description}</p>
        )}
        <div className="flex items-center gap-3">
          <Label htmlFor={`enabled-${rule.id}`}>Enabled</Label>
          <Button
            id={`enabled-${rule.id}`}
            variant={enabled ? 'default' : 'outline'}
            size="sm"
            onClick={() => setEnabled(!enabled)}
          >
            {enabled ? 'On' : 'Off'}
          </Button>
        </div>
        <div className="space-y-1">
          <Label htmlFor={`channels-${rule.id}`}>Channels (comma-separated)</Label>
          <Input
            id={`channels-${rule.id}`}
            value={channels}
            onChange={(e) => setChannels(e.target.value)}
            placeholder="default, slack-ops"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`params-${rule.id}`}>Params (JSON)</Label>
          <textarea
            id={`params-${rule.id}`}
            value={paramsJson}
            onChange={(e) => setParamsJson(e.target.value)}
            rows={4}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
          />
        </div>
        <Button size="sm" disabled={updateMut.isPending} onClick={() => void handleSave()}>
          Save
        </Button>
      </CardContent>
    </Card>
  )
}

export function AlertRulesPage() {
  const { data, isLoading } = useAlertRules()

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold tracking-tight">Alert Rules</h1>
      {isLoading && <Skeleton className="h-48" />}
      {data && data.length === 0 && (
        <p className="text-sm text-muted-foreground">No alert rules defined.</p>
      )}
      {data && data.map((rule) => <RuleRow key={rule.id} rule={rule} />)}
    </div>
  )
}
